/**
 * Geometry of the sock measurement template (the pre-printed size drawing with
 * the letter-coded dimension overlay). Shared by the on-screen drawing
 * (`SockMeasurementDrawing.vue`) and the technical passport PDF export so both
 * place the same letters on the same parts of the same image.
 */

export type SockDrawingKey = 'uzun' | 'orta' | 'kalta' | 'sledi'
export type MeasurementKey = 'cuffHeight' | 'cuffWidth' | 'overallHeight' | 'footWidth' | 'footLength'
export type MeasurementTone = 'red' | 'green' | 'blue' | 'purple' | 'teal'

export interface MeasurementPlacement {
  key: MeasurementKey
  x: number
  y: number
  tone: MeasurementTone
  anchor?: 'start' | 'middle' | 'end'
}

/** Overlay coordinate space of the drawing (the SVG `viewBox`). */
export const MEASUREMENT_TEMPLATE_SIZE = 1024

export const MEASUREMENT_LETTERS: Record<MeasurementKey, string> = {
  overallHeight: 'A',
  footLength: 'C',
  cuffWidth: 'D',
  footWidth: 'E',
  cuffHeight: 'G',
}

export const MEASUREMENT_TONE_COLORS: Record<MeasurementTone, string> = {
  red: '#c0392b',
  green: '#2e8b57',
  teal: '#0f8b8d',
  purple: '#7b4fa0',
  blue: '#2f6fb5',
}

/** Colour of a letter whose value has not been entered yet. */
export const MEASUREMENT_EMPTY_COLOR = '#8a9792'

const longSockPlacements: MeasurementPlacement[] = [
  { key: 'cuffWidth', x: 562, y: 86, tone: 'teal' },
  // G sits inside the cuff-height box (y 170-261 on the drawing), a little below its top edge.
  { key: 'cuffHeight', x: 322, y: 250, tone: 'red', anchor: 'end' },
  // A rides the long vertical height line (y 170-632): centred, not pushed to the bottom.
  { key: 'overallHeight', x: 812, y: 408, tone: 'blue', anchor: 'start' },
  { key: 'footWidth', x: 285, y: 900, tone: 'purple' },
  // C sits at the heel end of the diagonal foot-length line, outside the sock outline.
  { key: 'footLength', x: 700, y: 660, tone: 'green', anchor: 'start' },
]

export const MEASUREMENT_PLACEMENTS: Record<SockDrawingKey, MeasurementPlacement[]> = {
  uzun: longSockPlacements,
  orta: longSockPlacements,
  kalta: [
    { key: 'cuffWidth', x: 637, y: 168, tone: 'teal' },
    // The ankle cuff-height box is only y 289-322 tall, so G moves down into it.
    { key: 'cuffHeight', x: 486, y: 308, tone: 'red', anchor: 'end' },
    { key: 'overallHeight', x: 775, y: 500, tone: 'blue', anchor: 'start' },
    { key: 'footWidth', x: 330, y: 745, tone: 'purple' },
    { key: 'footLength', x: 610, y: 305, tone: 'green' },
  ],
  sledi: [
    { key: 'cuffWidth', x: 512, y: 64, tone: 'teal' },
    { key: 'cuffHeight', x: 330, y: 128, tone: 'red', anchor: 'end' },
    { key: 'overallHeight', x: 745, y: 520, tone: 'blue', anchor: 'start' },
    { key: 'footWidth', x: 560, y: 982, tone: 'purple' },
    { key: 'footLength', x: 420, y: 928, tone: 'green' },
  ],
}

/** The drawing that belongs to a passport `sock_type` (short sock is the fallback). */
export function sockDrawingKey(sockType?: string | null): SockDrawingKey {
  const key = sockType?.trim().toLowerCase()
  return key === 'uzun' || key === 'orta' || key === 'sledi' ? key : 'kalta'
}

function toCentimetres(value?: number | null): number | null {
  const numeric = Number(value)
  if (value == null || !Number.isFinite(numeric) || numeric <= 0) return null
  return numeric / 10
}

/** Millimetres in, two-decimal centimetre string out (`215` → `21.50`). */
export function formatMeasurementCm(value?: number | null): string | null {
  const centimetres = toCentimetres(value)
  return centimetres === null ? null : centimetres.toFixed(2)
}

export interface MeasurementOverlayLabel {
  key: MeasurementKey
  code: string
  /** Two-decimal centimetre value, or null when the passport has no value yet. */
  value: string | null
  /** What the drawing prints: `A 21.50`, or just the code when the value is missing. */
  text: string
  color: string
}

export interface MeasurementDrawingInput {
  cuff_height_mm?: number | null
  cuff_width_mm?: number | null
  foot_width_mm?: number | null
  foot_length_mm?: number | null
  leg_height_mm?: number | null
}

/**
 * Letter/value labels of one drawing, in placement order. Values come from the
 * mm dimensions (`TechnicalPassportDimensions`); millimetres are shown as cm.
 */
export function measurementOverlayLabels(
  dimensions: MeasurementDrawingInput,
  sockType?: string | null,
): MeasurementOverlayLabel[] {
  const values: Record<MeasurementKey, string | null> = {
    cuffHeight: formatMeasurementCm(dimensions.cuff_height_mm),
    cuffWidth: formatMeasurementCm(dimensions.cuff_width_mm),
    overallHeight: formatMeasurementCm(dimensions.leg_height_mm),
    footWidth: formatMeasurementCm(dimensions.foot_width_mm),
    footLength: formatMeasurementCm(dimensions.foot_length_mm),
  }
  return MEASUREMENT_PLACEMENTS[sockDrawingKey(sockType)].map((placement) => {
    const code = MEASUREMENT_LETTERS[placement.key]
    const value = values[placement.key]
    return {
      key: placement.key,
      code,
      value,
      text: value ? `${code} ${value}` : code,
      color: value ? MEASUREMENT_TONE_COLORS[placement.tone] : MEASUREMENT_EMPTY_COLOR,
    }
  })
}
